import { createContext, useContext, useCallback } from 'react';
import { useFormValidation } from '../hooks/useFormValidation';
import { ValidationRule } from '@/types/common';

interface FormContextValue {
  values: Record<string, any>;
  errors: Record<string, string>;
  setFieldValue: (field: string, value: any) => void;
  validate: () => boolean;
}

const FormContext = createContext<FormContextValue | null>(null);

interface FormProviderProps {
  children: React.ReactNode;
  initialValues: Record<string, any>;
  validationRules?: Record<string, ValidationRule[]>;
  onSubmit?: (values: Record<string, any>) => void;
}

export function FormProvider({ 
  children, 
  initialValues,
  validationRules = {},
  onSubmit 
}: FormProviderProps) {
  const { errors, validate } = useFormValidation(validationRules);
  
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validate() && onSubmit) {
      onSubmit(initialValues);
    }
  }, [validate, onSubmit, initialValues]);

  return (
    <FormContext.Provider value={{
      values: initialValues,
      errors,
      setFieldValue: (field, value) => {
        initialValues[field] = value;
      },
      validate
    }}>
      <form onSubmit={handleSubmit}>
        {children}
      </form>
    </FormContext.Provider>
  );
}

export const useForm = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
};